import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { unstable_useBlocker, useNavigate, useSearchParams } from 'react-router-dom';
import { unstable_Blocker as Blocker, unstable_BlockerFunction as BlockerFunction } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppSelector } from '@/app/hook';
import { confirmWithDialog } from '@/components/common/confirmation/confirm-dialog';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import TextScenarioComponent from '../../components/dashboard/knowledge-bases-create/scenario/text-scenario/text-scenario.component';
import VideoScenarioComponent from '../../components/dashboard/knowledge-bases-create/scenario/video-scenario/video-scenario.component';

const McBotFaceEditor = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentScenarioStatus = useAppSelector(state => state.bots.wizard.status);

  const [hasUnpublishedData, setHasUnpublishedData] = React.useState(currentScenarioStatus === 'not_published' ? true : false);

  // Allow the submission navigation to the same route to go through
  const shouldBlock = React.useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => hasUnpublishedData && currentLocation.pathname !== nextLocation.pathname,
    [hasUnpublishedData]
  );
  const blocker = unstable_useBlocker(shouldBlock);

  // Reset the blocker if the user publish data
  React.useEffect(() => {
    if (blocker.state === 'blocked' && !hasUnpublishedData) {
      blocker.reset();
    }
  }, [blocker, hasUnpublishedData]);

  useEffect(() => {
    if (!searchParams.has('type') || !['text-scenario', 'video-scenario'].includes(searchParams.get('type') as string)) {
      toast.error('Selected bot type was not recognized as McBotFace instance');

      navigate('../');
    }
  }, [searchParams]);

  function ConfirmNavigation({ blocker }: { blocker: Blocker }) {
    useEffect(() => {
      let active = true;

      const handleConfirm = async () => {
        const confirmed = await confirmWithDialog(t('KnowledgeBasesCreateConditionalScenario/text-06'), {
          confirmButtonText: t('GlobalConfirmationModal/confirm'),
          cancelButtonText: t('GlobalConfirmationModal/cancel'),
          confirmButtonStyle: 'warning',
          cancelButtonStyle: 'secondary',
        });
        if (!active) return; // user already navigated away or component unmounted

        if (confirmed) {
          blocker.proceed?.(); // allow navigation
        } else {
          blocker.reset?.(); // cancel navigation
        }
      };

      if (blocker.state === 'blocked') {
        handleConfirm();
      }

      return () => {
        active = false;
      };
    }, [blocker]);

    return null;
  }

  const typeMapper: { [key: string]: React.ReactNode } = {
    'text-scenario': <TextScenarioComponent {...{ hasUnpublishedData, setHasUnpublishedData }} />,
    'video-scenario': <VideoScenarioComponent {...{ hasUnpublishedData, setHasUnpublishedData }} />,
  };
  return (
    <React.Fragment>
      {typeMapper[searchParams.get('type') as string]}

      {blocker ? <ConfirmNavigation blocker={blocker} /> : null}
    </React.Fragment>
  );
};

export default WrappedErrorBoundary(McBotFaceEditor);
